<template>
    <ui5-dialog
        ref="dialog"
        :header-text="headerText"
        @after-close="$emit('close')"
        :stretch="true"
    >
        <pre
            v-if="isDebug"
            class="debug-fom"
            >{{ { form, sourceId, groups } }}</pre
        >
        <div class="form-container">
            <ui5-label
                for="targetGroupInput"
                required
            >
                {{ trans('pages.product_type_groups.select_target_group') }}
            </ui5-label>
            <auto-complete-input
                id="targetGroupInput"
                name="target-group"
                :value="form.targetId"
                :options="groupOptions"
                :disabled="saving"
                :placeholder="trans('pages.product_type_groups.select_group')"
                :lbl-no-options="
                    trans('pages.product_type_groups.no_groups_found')
                "
                @input="form.targetId = $event"
            />
        </div>
        <div
            slot="footer"
            class="dialog-footer"
        >
            <ui5-button
                @click="save"
                :disabled="saving || !form.targetId"
            >
                <spinner v-if="saving" />
                {{ trans('pages.product_type_groups.merge') }}
            </ui5-button>
            <ui5-button
                @click="close"
                :disabled="saving"
            >
                {{ trans('pages.product_type_groups.cancel') }}
            </ui5-button>
        </div>
    </ui5-dialog>
</template>

<script>
import IsDebugMixin from '../../mixins/is-debug';
import localization from '../../mixins/localization';
import Spinner from '../loader/Spinner';
import AutoCompleteInput from '../AutoCompleteInput.vue';
import { getItemPath } from '../../shared/nestedStructure';
import { byLabelSorter } from '../../shared/collections';

export default {
    name: 'ProductTypeGroupMergeForm',
    mixins: [localization, IsDebugMixin],
    components: { Spinner, AutoCompleteInput },
    props: {
        sourceId: {
            type: [Number, null],
            default: null,
        },
        groups: {
            type: Array,
            default: () => [],
        },
        saving: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            form: {
                targetId: null,
            },
        };
    },
    computed: {
        availableGroups() {
            if (!this.sourceId) return [];
            const descendantIds = this.getDescendantIds(this.sourceId);
            return this.groups.filter(
                (g) => g.id !== this.sourceId && !descendantIds.includes(g.id)
            );
        },
        groupOptions() {
            return this.availableGroups
                .map((group) => ({
                    value: group.id,
                    label: getItemPath(
                        group.id,
                        this.availableGroups,
                        this.isDebug
                    ),
                }))
                .sort(byLabelSorter);
        },
        headerText() {
            if (!this.sourceId)
                return this.trans('pages.product_type_groups.merge');
            const sourcePath = getItemPath(
                this.sourceId,
                this.availableGroups,
                this.isDebug
            );
            return `${this.trans(
                'pages.product_type_groups.merge'
            )}: ${sourcePath}`;
        },
    },
    watch: {
        sourceId(newVal) {
            if (!newVal) {
                this.form.targetId = null;
            }
        },
    },
    methods: {
        show() {
            this.form.targetId = null;
            this.$refs.dialog.show();
        },
        close() {
            this.$refs.dialog.close();
        },
        save() {
            if (this.saving || !this.form.targetId) return;
            const payload = {
                source_id: this.sourceId,
                target_id: this.form.targetId,
            };
            this.$emit('save', payload);
        },
        getDescendantIds(sourceId) {
            const descendants = [];
            const findDescendants = (groupId) => {
                const group = this.groups.find((g) => g.id === groupId);
                if (group && group.items && group.items.length) {
                    group.items.forEach((item) => {
                        descendants.push(item.id);
                        findDescendants(item.id);
                    });
                }
            };
            findDescendants(sourceId);
            return descendants;
        },
    },
};
</script>

<style
    lang="scss"
    scoped
>
.debug-fom {
    max-width: 100%;
    max-height: 150px;
    overflow: scroll;
}
.form-container {
    padding: 1em;
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    min-width: 300px;

    ui5-label {
        font-weight: bold;
        margin-bottom: 0.2em;
    }
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5em;
    padding: 0.5em;
}

.dialog-footer ui5-button {
    display: flex;
    align-items: center;
    gap: 0.5em;
}
</style>
